import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ChevronLeft, LucideAngularModule, Save, Search, ShieldCheck } from 'lucide-angular';
import { UserMenuPrivilege, VendorUser } from '../../../core/models/domain.models';
import { VendorsService } from '../services/vendors.service';
import { ToastService } from '../../../shared/ui/toast.service';

@Component({
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './vendor-user-privileges.page.html',
  styleUrl: './vendor-user-privileges.page.scss',
})
export class VendorUserPrivilegesPage implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly vendorsService = inject(VendorsService);
  private readonly toast = inject(ToastService);

  protected readonly ChevronLeftIcon = ChevronLeft;
  protected readonly ShieldCheckIcon = ShieldCheck;
  protected readonly SearchIcon = Search;
  protected readonly SaveIcon = Save;

  protected readonly vendorId = signal('');
  protected readonly userId = signal('');
  protected readonly user = signal<VendorUser | null>(null);
  protected readonly rows = signal<UserMenuPrivilege[]>([]);
  protected readonly loading = signal(false);
  protected readonly saving = signal(false);
  protected readonly error = signal('');
  protected readonly search = signal('');

  protected readonly filteredRows = computed(() => {
    const term = this.search().trim().toLowerCase();
    const visibleRows = this.rows().filter((row) => !this.isContainerOnlyRow(row));
    if (!term) return visibleRows;
    return visibleRows.filter((row) => {
      const parent = (row.parent_name || '').toLowerCase();
      return row.menu_name.toLowerCase().includes(term) || row.menu_list.toLowerCase().includes(term) || parent.includes(term);
    });
  });

  protected readonly groupedRows = computed(() => {
    const groups = new Map<string, UserMenuPrivilege[]>();
    for (const row of this.filteredRows()) {
      const key = row.menu_list || 'General';
      const current = groups.get(key) || [];
      current.push(row);
      groups.set(key, current);
    }
    return Array.from(groups.entries()).map(([label, items]) => ({ label, items }));
  });

  protected readonly allReadSelected = computed(() => this.filteredRows().length > 0 && this.filteredRows().every((row) => row.can_read));
  protected readonly allWriteSelected = computed(() => this.filteredRows().length > 0 && this.filteredRows().every((row) => row.can_write));

  async ngOnInit(): Promise<void> {
    this.vendorId.set(this.route.snapshot.paramMap.get('id') || '');
    this.userId.set(this.route.snapshot.paramMap.get('userId') || '');
    await this.loadPage();
  }

  protected async loadPage(): Promise<void> {
    const vendorId = this.vendorId();
    const userId = this.userId();
    if (!vendorId || !userId) {
      this.error.set('Vendor user id is missing.');
      return;
    }

    this.loading.set(true);
    this.error.set('');
    try {
      const [users, rows] = await Promise.all([
        firstValueFrom(this.vendorsService.listUsers(vendorId)),
        firstValueFrom(this.vendorsService.listUserPrivileges(vendorId, userId)),
      ]);
      this.user.set((users || []).find((item) => item.id === userId) || null);
      this.rows.set(rows);
    } catch {
      this.error.set('Failed to load privilege details.');
    } finally {
      this.loading.set(false);
    }
  }

  protected goBack(): void {
    void this.router.navigate(['/vendors', this.vendorId(), 'edit']);
  }

  protected onSearch(value: string): void {
    this.search.set(value);
  }

  protected toggleAll(field: 'can_read' | 'can_write', checked: boolean): void {
    const filteredIds = new Set(this.filteredRows().map((row) => row.menu_id));
    this.rows.update((current) =>
      current.map((row) => {
        if (!filteredIds.has(row.menu_id)) return row;
        if (field === 'can_read') {
          return { ...row, can_read: checked, can_write: checked ? row.can_write : false };
        }
        return { ...row, can_read: checked ? true : row.can_read, can_write: checked };
      }),
    );
  }

  protected isGroupSelected(items: UserMenuPrivilege[], field: 'can_read' | 'can_write'): boolean {
    return items.length > 0 && items.every((item) => (field === 'can_read' ? item.can_read : item.can_write));
  }

  protected toggleGroup(items: UserMenuPrivilege[], field: 'can_read' | 'can_write', checked: boolean): void {
    const groupIds = new Set(items.map((item) => item.menu_id));
    this.rows.update((current) =>
      current.map((row) => {
        if (!groupIds.has(row.menu_id)) return row;
        if (field === 'can_read') {
          return { ...row, can_read: checked, can_write: checked ? row.can_write : false };
        }
        return { ...row, can_read: checked ? true : row.can_read, can_write: checked };
      }),
    );
  }

  protected toggleRow(row: UserMenuPrivilege, field: 'can_read' | 'can_write', checked: boolean): void {
    const targetIds = new Set(this.descendantMenuIds(row.menu_id));
    targetIds.add(row.menu_id);
    this.rows.update((current) =>
      current.map((item) => {
        if (!targetIds.has(item.menu_id)) return item;
        if (field === 'can_read') {
          return { ...item, can_read: checked, can_write: checked ? item.can_write : false };
        }
        return { ...item, can_read: checked ? true : item.can_read, can_write: checked };
      }),
    );
  }

  protected async savePrivileges(): Promise<void> {
    this.saving.set(true);
    this.error.set('');
    try {
      const rows = await firstValueFrom(
        this.vendorsService.saveUserPrivileges(this.vendorId(), this.userId(), {
          items: this.rows().map((row) => ({
            menu_id: row.menu_id,
            can_read: row.can_read,
            can_write: row.can_write,
          })),
        }),
      );
      this.rows.set(rows);
      this.toast.show('Privilege assigned successfully', '', 'success');
    } catch {
      this.error.set('Failed to save privilege changes.');
    } finally {
      this.saving.set(false);
    }
  }

  protected isParentRow(row: UserMenuPrivilege): boolean {
    return row.level <= 1 || row.menu_url === '#' || this.rows().some((item) => item.parent_id === row.menu_id);
  }

  protected isContainerOnlyRow(row: UserMenuPrivilege): boolean {
    return row.menu_url === '#';
  }

  private descendantMenuIds(menuId: string): string[] {
    const children = this.rows().filter((row) => row.parent_id === menuId);
    return children.flatMap((child) => [child.menu_id, ...this.descendantMenuIds(child.menu_id)]);
  }
}
